/**
 * Utilities for handling truncated and reply-marker bridged ActivityPub content.
 */

import {
    isTruncatedBridgedPost,
    stripTruncationMarker,
    getCollapsedDisplayText,
} from '@johnwhiteside/bridgy-fed-sdk'
import { isLikelyFediversePostUrl, isSameFediversePost } from '@/lib/embed'
import type { Post } from '@/types/posts'

export {
    isTruncatedBridgedPost,
    stripBridgyBoilerplate,
    stripTruncationMarker,
    getCollapsedDisplayText,
} from '@johnwhiteside/bridgy-fed-sdk'

// Matches only when the "RE: <url>" marker occupies a whole line of its own —
// which is where Mastodon-family clients put it, either above the author's
// comment or below it. Requiring a line boundary on both sides means a
// genuine mention of "RE:" mid-sentence is left alone. Captures the URL so
// callers can compare it against a rendered card's URL.
const QUOTE_REPLY_MARKER_RE = /(?:^|\n)[^\S\n]*re:[^\S\n]*(https?:\/\/\S+)[^\S\n]*(?=\n|$)/i

/**
 * Extracts the URL from a "RE: <url>" marker, if present. `\S+` runs to
 * whitespace, so trailing punctuation from surrounding prose (a closing
 * paren, a period) rides along — trimmed off before comparison.
 */
function quoteReplyMarkerUrl(text: string): string | undefined {
    return QUOTE_REPLY_MARKER_RE.exec(text)?.[1]?.replace(/[.,;:)\]]+$/, '')
}

/**
 * Strip a fediverse client's literal "RE: <url>" quote-reply fallback text.
 * Mastodon-family apps write this as post body when quoting a URL, since
 * ActivityPub has no native quote-post concept; Bridgy Fed carries it over
 * verbatim into the bridged AT Protocol record. When that text is shown
 * inside a quote embed card, the card already conveys the referenced post,
 * so the marker duplicates it and is stripped as boilerplate.
 */
export function stripQuoteReplyMarker(text: string): string {
    return text.replace(QUOTE_REPLY_MARKER_RE, '').trim()
}

// Same marker, matched against an element's text content rather than raw post
// text: "RE: <url>" and nothing else. The scheme is optional because Mastodon
// renders link text in pieces (`<span>https://</span><span>host/@u/1</span>`),
// and some clients hide the scheme entirely.
const HTML_QUOTE_REPLY_MARKER_RE = /^re:\s*(?:https?:\/\/)?\S+$/i

/**
 * A "RE: <url>" quote-reply marker as its own paragraph — the shape Mastodon
 * serves it in (`<p class="quote-inline">RE: <a href="…">…</a></p>`, sometimes
 * with the contents wrapped in a `<span>`; the classes are gone by the time
 * `sanitizeHtml` is done with it). Requiring a single http(s) link keeps this
 * off a paragraph that merely opens with the letters "RE:".
 */
function isQuoteReplyMarkerElement(el: Element): boolean {
    if (el.tagName !== 'P') return false
    if (!HTML_QUOTE_REPLY_MARKER_RE.test((el.textContent ?? '').trim())) return false
    const links = el.querySelectorAll('a')
    return links.length === 1 && /^https?:\/\//i.test(links[0].getAttribute('href') ?? '')
}

/**
 * Strip a leading or trailing "RE: <url>" quote-reply marker paragraph from
 * full ActivityPub content HTML.
 *
 * The plain-text `stripQuoteReplyMarker` can't be reused here: this content is
 * HTML fetched from the origin instance, where the marker is a whole element
 * with the URL inside an `<a>`, so a regex over the serialized markup would
 * either miss it or leave an empty paragraph behind. Parse instead, and drop
 * the element only when it sits at the very start or end of the content —
 * where clients put the marker — so a genuine "RE: <url>" line mid-post is
 * left alone.
 *
 * Operates on already-sanitized HTML and only ever removes nodes, so it can't
 * reintroduce anything `sanitizeHtml` took out.
 */
export function stripQuoteReplyMarkerFromHtml(html: string): string {
    if (!html) return html
    const body = new DOMParser().parseFromString(html, 'text/html').body
    const children = Array.from(body.children)
    if (children.length === 0) return html

    const candidates = new Set([children[0], children[children.length - 1]])
    let stripped = false
    for (const el of candidates) {
        if (isQuoteReplyMarkerElement(el)) {
            el.remove()
            stripped = true
        }
    }
    return stripped ? body.innerHTML : html
}

/**
 * Bridgy Fed attaches its "Original post on <domain>" external card for two
 * different reasons. One is truncation (see the `isTruncated` guard in
 * `PostEmbedDisplay`/`QuotedPostEmbed`). The other is an attachment it could
 * not bridge — audio is the observed case, titled e.g. "[Audio] Original post
 * on universeodon.com" — and then the text carries no "[…]" marker at all, so
 * the truncation guard misses it and `PostEmbedDisplay` resolves the
 * self-pointing URL into a `FediverseEmbed`: a quote-post card of the very
 * post the reader is already looking at.
 *
 * This is NOT a hide condition. Unlike the truncated case — where the app's
 * own "Show more" expander already carries the same content — this card is
 * the only handle the reader has on the unbridged attachment. Callers pass
 * the result as `PostEmbedDisplay`'s `excludeFediverse`, degrading it to a
 * plain link card that still opens the original post.
 */
export function isSelfReferencingBridgedCard(
    post: Pick<Post, 'embed' | 'bridgyOriginalUrl'>
): boolean {
    const external = post.embed?.type === 'external' ? post.embed.external : undefined
    if (!post.bridgyOriginalUrl || !external) return false
    return isSameFediversePost(external.uri, post.bridgyOriginalUrl)
}

// The read-more pointer as a trailing line of post text: "[Original post on
// <domain>]". Anchored to the end because that is where Bridgy Fed appends it,
// and `[^\]\n]+` keeps it to a single bracketed segment so a bracket elsewhere
// in the post can't extend the match.
const READ_MORE_POINTER_RE = /\s*\[original post on [^\]\n]+\]\s*$/i

/**
 * Bridgy Fed's "read more" pointer has two forms, and which one it uses depends
 * on whether the AT Protocol embed slot is free. With no media it attaches an
 * `app.bsky.embed.external` "Original post on <domain>" card — which
 * `PostEmbedDisplay`/`QuotedPostEmbed` already suppress on truncated posts via
 * their `isTruncated` guard. When the post carries a video or images that slot
 * is taken, so the pointer is appended to the post *text* instead, as a
 * trailing "[Original post on <domain>]" line — and nothing suppressed that.
 *
 * The text form is redundant with the app's own "Show more" expansion for the
 * same content (`FullAPContentExpander`), exactly as the card form is.
 * Stripping it also restores the `[…]` truncation marker to the end of the
 * text, which is where the SDK's `stripTruncationMarker` expects it — with the
 * pointer still trailing, its end-anchored regex misses, so the raw marker
 * stays visible mid-text and `getCollapsedDisplayText` appends its ellipsis
 * after the pointer rather than at the cut-off point.
 *
 * `isTruncatedBridgedPost` is a pure text-pattern check, so this is scoped to
 * posts with a `bridgyOriginalUrl` — set only on records Bridgy Fed actually
 * created from ActivityPub content — leaving a native AT Protocol post that
 * happens to end in that literal text alone.
 */
export function stripBridgedReadMorePointer(
    post: Pick<Post, 'text' | 'bridgyOriginalUrl'>
): string {
    if (!post.bridgyOriginalUrl || !isTruncatedBridgedPost(post.text)) return post.text
    return post.text.replace(READ_MORE_POINTER_RE, '')
}

/**
 * Whether a card conveying the referenced post renders alongside this post's
 * text — the condition under which a "RE: <url>" marker is redundant.
 *
 * `quotedPost` is only one of the two ways that card appears, and is checked
 * first, unconditionally: a real record embed renders the quoted post itself,
 * whose AT URI isn't comparable to a fediverse URL, so today's behavior is
 * kept as-is there. Bridgy Fed bridges a Mastodon quote as an
 * `app.bsky.embed.external` link pointing at the quoted status (not an
 * `app.bsky.embed.record` ref), so `quotedPost` stays undefined;
 * `PostEmbedDisplay` then recognizes the fediverse post URL and renders
 * `FediverseEmbed`, which resolves it into the same `QuotedPostEmbed` card.
 * Gating on `quotedPost` alone therefore missed every bridged quote.
 *
 * For that external-card path, identity — not just shape — is required: a
 * post can carry an external card for a *different* post than the one its
 * "RE:" line names (including the self-referencing card from
 * `isSelfReferencingBridgedCard`, which points at the post's own original,
 * not whatever the marker references), and stripping in that case would
 * delete the reader's only pointer to what the marker actually named. The
 * card's URL often differs in *form* from the one in the marker text
 * (`/@user/123` vs `/users/user/statuses/123`) even when it's the same post —
 * `isSameFediversePost` accounts for that.
 *
 * The truncation check mirrors `PostEmbedDisplay`'s own `isTruncated` guard,
 * which drops external embeds entirely on truncated bridged posts: with no card
 * rendering, the marker is the only pointer to the referenced post and has to
 * stay.
 */
export function hasQuoteCardReference(post: Pick<Post, 'text' | 'quotedPost' | 'embed'>): boolean {
    if (post.quotedPost) return true
    const external = post.embed?.type === 'external' ? post.embed.external : undefined
    if (!external || isTruncatedBridgedPost(post.text)) return false
    if (!isLikelyFediversePostUrl(external.uri, external.title)) return false
    const markerUrl = quoteReplyMarkerUrl(post.text)
    // No marker to compare against the card — behavior-neutral either way: the
    // only consumers are getBridgedPostDisplayText (stripQuoteReplyMarker is a
    // no-op on markerless text) and FullAPContentExpander's hasQuoteReference,
    // whose HTML-strip path only renders when isTruncated, already false above.
    if (!markerUrl) return false
    return isSameFediversePost(markerUrl, external.uri)
}

/**
 * Display text for a post's collapsed (pre-expansion) render: the bridged
 * read-more pointer stripped first, then the "RE: <url>" quote-reply marker
 * (when a quote embed will render alongside it), then the truncation marker
 * handled.
 *
 * Order matters — the truncation-marker strip is end-anchored, so both the
 * pointer and the RE: marker strips have to go before it runs. Neither of
 * those two touches the other's target text (the pointer is trailing, the RE:
 * marker sits on its own line), so their relative order doesn't matter.
 * ActivityPub-sourced posts get the SDK's ellipsis at the cut-off point;
 * AT-sourced posts just lose the marker.
 *
 * The RE: marker strip shares `hasQuoteCardReference` with the `hasQuoteReference`
 * gating used for the expanded ("Show more") text in these same components, so
 * both states agree on whether a quote card conveys the reference.
 */
export function getBridgedPostDisplayText(
    post: Pick<Post, 'text' | 'bridgyOriginalUrl' | 'isActivityPub' | 'quotedPost' | 'embed'>
): string {
    const pointerStripped = stripBridgedReadMorePointer(post)
    const text = hasQuoteCardReference(post)
        ? stripQuoteReplyMarker(pointerStripped)
        : pointerStripped
    return post.isActivityPub ? getCollapsedDisplayText(text) : stripTruncationMarker(text)
}
